import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../Firebase';
import { collection, getDocs } from 'firebase/firestore';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  FiActivity, FiHeart, FiCpu, FiSmile,
  FiShield, FiUser, FiGrid, FiArrowRight
} from 'react-icons/fi';

gsap.registerPlugin(ScrollTrigger);

// Custom helper to map department IDs to professional medical icons
const getDepartmentIcon = (id) => {
  switch (id) {
    case 'orthopedic-trauma':
      return <FiActivity className="icon-svg" />;
    case 'obgyn':
      return <FiHeart className="icon-svg" />;
    case 'general-surgery':
      return <FiActivity className="icon-svg" />;
    case 'plastic-surgery':
      return <FiSmile className="icon-svg" />;
    case 'laparoscopic-surgery':
      return <FiCpu className="icon-svg" />;
    case 'advanced-orthopedics':
      return <FiShield className="icon-svg" />;
    case 'infertility':
      return <FiUser className="icon-svg" />;
    case 'high-risk-obstetrics':
      return <FiHeart className="icon-svg" />;
    case 'urology':
      return <FiActivity className="icon-svg" />;
    case 'oncology':
      return <FiActivity className="icon-svg" />;
    case 'preventive-health':
      return <FiGrid className="icon-svg" />;
    case 'cardiology':
      return <FiHeart className="icon-svg" />;
    case 'internal-medicine':
      return <FiUser className="icon-svg" />;
    default:
      return null;
  }
};

const Departments = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const snap = await getDocs(collection(db, 'services'));
        const servicesList = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDepartments(servicesList);
      } catch (err) {
        console.error("Error fetching services:", err);
      }
    };
    fetchServices();
  }, []);

  const handleLearnMore = (deptId) => {
    navigate('/services', { state: { departmentId: deptId } });
  };

  useEffect(() => {
    // Smooth stagger entry animation on scroll using GSAP
    if (departments.length > 0) {
      const ctx = gsap.context(() => {
        gsap.fromTo(
          '.service-card',
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: '.services-grid',
              start: 'top 85%',
              toggleActions: 'play none none none'
            }
          }
        );
      }, sectionRef);
      return () => ctx.revert();
    }
  }, [departments]);

  return (
    <section id="services" className="section" ref={sectionRef}>
      <div className="section-header">
        <span className="section-tag">Centres of Excellence</span>
        <h2 className="section-title">Specialized Medical Care</h2>
        <p className="section-subtitle">
          Expert care across Orthopaedics, General Surgery, Women's Health, Anaesthesia and other essential specialties, delivered with modern treatment approaches and a patient-first focus at Swarup Hospital, Kolhapur.
        </p>
      </div>

      <div className="services-grid">
        {departments.slice(0, 4).map((dept) => {
          const professionalIcon = getDepartmentIcon(dept.id);
          return (
            <article key={dept.id} className="service-card">
              <div className="service-card-glow" />
              <div className="service-icon" aria-hidden="true">
                {professionalIcon ? professionalIcon : <span className="emoji-icon">{dept.icon}</span>}
              </div>
              <h3>{dept.name}</h3>
              <p>{dept.description}</p>
              <button
                className="service-link"
                onClick={() => handleLearnMore(dept.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
              >
                Learn More <FiArrowRight className="arrow-icon" />
              </button>
            </article>
          );
        })}
      </div>

      {/* Explore More Button */}
      <div style={{ textAlign: 'center', marginTop: '48px' }}>
        <button
          onClick={() => navigate('/services')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            padding: '16px 38px',
            background: 'var(--gradient-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontSize: '1.05rem',
            fontWeight: '800',
            fontFamily: "'Poppins', sans-serif",
            cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(1, 111, 107, 0.25)',
            transition: 'all 0.3s ease',
          }}
          onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(1,111,107,0.4)'; }}
          onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(1, 111, 107, 0.25)'; }}
        >
          Explore More Services <FiArrowRight />
        </button>
      </div>
    </section>

  );
};

export default Departments;
